import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  ScrollArea,
  DeleteConfirmDialog,
  Badge,
} from 'design-system'
import {
  dequeue,
  getFailedAndConflictMutations,
  requeueConflictMutations,
  requeueFailedMutations,
  requeueMutationById,
  type QueuedMutation,
} from 'store/offlineQueue'
import { replayQueue } from 'slices/offlineMiddleware'
import { flushOfflineBootstrapPendingCounts } from 'services/offlineBootstrap'
import { checkConnectivity } from 'utils/connectivityCheck'
import { erpToast } from 'design-system/components/erp-toast'
import { RotateCcw, Trash2 } from 'lucide-react'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCountsMayHaveChanged: () => Promise<void>
}

function mutationLabel(item: QueuedMutation, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const actionType = item.actionType || item.type || ''
  const key = `offline.mutationActions.${actionType}`
  const translated = t(key)
  if (translated !== key) return translated
  return actionType || item.sliceName || item.url
}

export function FailedMutationsDialog({ open, onOpenChange, onCountsMayHaveChanged }: Props) {
  const { t } = useTranslation()
  const [items, setItems] = useState<QueuedMutation[]>([])
  const [dismissId, setDismissId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [retryAllBusy, setRetryAllBusy] = useState(false)

  const reload = useCallback(async () => {
    setItems(await getFailedAndConflictMutations())
  }, [])

  useEffect(() => {
    if (!open) return
    void reload()
  }, [open, reload])

  useEffect(() => {
    const onEvt = () => {
      void reload()
    }
    window.addEventListener('offline-bootstrap:updated', onEvt)
    return () => window.removeEventListener('offline-bootstrap:updated', onEvt)
  }, [reload])

  const refreshBootstrap = async () => {
    await flushOfflineBootstrapPendingCounts()
    await onCountsMayHaveChanged()
  }

  const handleRetry = async (id: string) => {
    if (!(await checkConnectivity())) {
      erpToast.warning(t('common.errors.noInternet'))
      return
    }
    setBusyId(id)
    try {
      const ok = await requeueMutationById(id)
      if (!ok) {
        erpToast.error(t('common.failedMutations.retryQueueFailed'))
        return
      }
      const result = await replayQueue()
      await reload()
      await refreshBootstrap()
      if (result.errors.length) {
        erpToast.warning(result.errors.slice(0, 2).join(' · '))
      } else {
        erpToast.success(t('common.failedMutations.syncSuccess'))
      }
    } finally {
      setBusyId(null)
    }
  }

  const handleRetryAll = async () => {
    if (!(await checkConnectivity())) {
      erpToast.warning(t('common.errors.noInternet'))
      return
    }
    setRetryAllBusy(true)
    try {
      const nFailed = await requeueFailedMutations()
      const nConflict = await requeueConflictMutations()
      if (nFailed + nConflict === 0) {
        erpToast.info(t('common.failedMutations.empty'))
        return
      }
      const result = await replayQueue()
      await reload()
      await refreshBootstrap()
      if (result.errors.length) {
        erpToast.warning(result.errors.slice(0, 2).join(' · '))
      } else {
        erpToast.success(t('common.failedMutations.syncSuccess'))
      }
    } finally {
      setRetryAllBusy(false)
    }
  }

  const handleDismiss = async (id: string) => {
    await dequeue(id)
    setDismissId(null)
    await reload()
    await refreshBootstrap()
    erpToast.success(t('common.failedMutations.dismissSuccess'))
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle>{t('common.failedMutations.title')}</DialogTitle>
            <DialogDescription>{t('common.failedMutations.description')}</DialogDescription>
          </DialogHeader>
          {items.length > 0 && (
            <div className="flex justify-end px-1 pb-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={retryAllBusy}
                onClick={() => {
                  void handleRetryAll()
                }}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('common.failedMutations.retryAll')}
              </Button>
            </div>
          )}
          <ScrollArea className="max-h-[min(60vh,420px)] pe-2">
            {items.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t('common.failedMutations.empty')}
              </p>
            ) : (
              <ul className="space-y-3">
                {items.map((entry) => (
                  <li
                    key={entry.id}
                    className="rounded-xl border border-border bg-card p-3 text-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="font-semibold">{mutationLabel(entry, t)}</p>
                          {entry.status === 'conflict' && (
                            <Badge variant="warning">{t('common.failedMutations.conflictBadge')}</Badge>
                          )}
                          {entry.status === 'failed' && (
                            <Badge variant="destructive">{t('common.failedMutations.failedBadge')}</Badge>
                          )}
                        </div>
                        {entry.last_error ? (
                          <p className="mt-0.5 text-xs text-muted-foreground">{entry.last_error}</p>
                        ) : null}
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          {entry.method} {entry.url}
                        </p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          {new Date(entry.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={busyId === entry.id || retryAllBusy}
                          onClick={() => void handleRetry(entry.id)}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDismissId(entry.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={dismissId != null}
        onOpenChange={(o) => {
          if (!o) setDismissId(null)
        }}
        onConfirm={() => dismissId && void handleDismiss(dismissId)}
        title={t('common.failedMutations.confirmDismissTitle')}
        description={t('common.failedMutations.confirmDismissDescription')}
        confirmText={t('common.failedMutations.confirmDismissConfirm')}
        cancelText={t('common.cancel')}
      />
    </>
  )
}

export default FailedMutationsDialog
